import * as React from "react";

export type AppHost = "mac" | "win";

/**
 * "Host" is where the Diagram app is running inside. It may be:
 * - "web": inside a browser, like Firefox or Safari, as a website
 * - "mac": in TablePlus Mac app, via [WkWebView][wk]
 * - "win": in TablePlus Win app, via [CefSharp][cs]
 *
 * Note that "host" is our own concept and is NOT the same as the native
 * "[navigator.platform][nv]".
 *
 * [wk]: https://developer.apple.com/documentation/webkit/wkwebview
 * [cs]: https://github.com/cefsharp/CefSharp
 * [nv]: https://developer.mozilla.org/en-US/docs/Web/API/NavigatorID/platform
 */
export type Host = "web" | AppHost;

/** Returns platform (mac/win) from navigator, used when forceAppHost */
export const getPlatformHost = (): Host => {
    const platform = navigator.platform.toLowerCase();
    if (platform.includes("mac")) return "mac";
    if (platform.includes("win")) return "win";
    return "web";
};

/** Returns @interface Host of current app */
const getHost = (): Host => {
    const webHostnames = [
        "localhost",
        "diagramplus.com",
        "beta.diagramplus.com",
        "diagram-plus.vercel.app",
        "192.168.0",
    ];
    if (webHostnames.includes(window.location.hostname)) return "web";
    return getPlatformHost();
};

export const host: Host = getHost();

export const isAppHost: boolean = host === "mac" || host === "win";

/** Set to true to force app UI when developing on localhost */
const ForceAppHostContext = React.createContext<
    [boolean, React.Dispatch<React.SetStateAction<boolean>>]
>([false, () => {}]);

export const ForceAppHostProvider = (props: { children: React.ReactNode }) => {
    // true = force app UI on localhost (for dev)
    const [forceAppHostUI, setForceAppHostUI] = React.useState(true);
    return React.createElement(ForceAppHostContext.Provider, {
        value: [forceAppHostUI, setForceAppHostUI],
        children: props.children,
    });
};

export const useIsAppHost = (): boolean => {
    const [forceAppHostUI] = React.useContext(ForceAppHostContext);
    return isAppHost || forceAppHostUI;
};

/** Use to toggle force app UI. Change initial useState(false) to useState(true) to default on. */
export const useForceAppHost = () => React.useContext(ForceAppHostContext);

/** Effective host for UI: when forceAppHost, returns platform (mac/win), else real host */
export const useHost = (): Host => {
    const [forceAppHostUI] = React.useContext(ForceAppHostContext);
    return forceAppHostUI ? getPlatformHost() : host;
};
